import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Home, Phone, Truck, ShieldCheck, HelpCircle, Sparkles } from 'lucide-react';
import SEO, { buildFAQSchema, buildLocalBusinessSchema, buildBreadcrumbSchema } from '../components/SEO';
import { UP_CITIES, SITE_URL, BRAND, CRILAM_FACTS } from '../lib/seoContent';

/**
 * Generic renderer for all UP city landing pages.
 * Route: /acrylic-boards-:citySlug — reads slug from route, matches UP_CITIES.
 */
const CityPage = ({ citySlug: cityProp }) => {
  const params = useParams();
  const citySlug = cityProp || params.citySlug;
  const city = UP_CITIES.find((c) => c.slug === citySlug);

  if (!city) {
    return (
      <div className="min-h-screen bg-[#F7F8F5] flex items-center justify-center px-6" data-testid="city-not-found">
        <div className="text-center max-w-md">
          <MapPin className="h-12 w-12 text-[#004534] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#004534] mb-3">City page not found</h1>
          <Link to="/" className="text-[#D4AF37] font-semibold hover:underline">Return home →</Link>
        </div>
      </div>
    );
  }

  const title = `Pre-Laminated Acrylic Boards in ${city.name} | CriLam Decorative`;
  const description = `Get CriLam's Pre-Laminated 6H Acrylic Boards with CriLam PUR-Bond™ technology in ${city.name}, UP — mirror finish, anti-yellowing, 100% waterproof, 10-year written warranty. Factory direct from Kanpur (${city.distance}).`;
  const url = `${SITE_URL}/acrylic-boards-${city.slug}`;

  const faqs = [
    {
      q: `Where can I buy Pre-Laminated Acrylic Boards in ${city.name}?`,
      a: `${BRAND} distributes Pre-Laminated 6H Acrylic Boards in ${city.name} directly from its Kanpur factory (${city.distance}). Contact ${CRILAM_FACTS.phone} or use the enquiry form at /contact to connect with the ${city.name} territory representative.`,
    },
    {
      q: `Does CriLam deliver to ${city.name}?`,
      a: `Yes. CriLam offers direct factory delivery to ${city.name} — ${city.distance}. Typical order-to-delivery timeline is 5-10 working days depending on customization.`,
    },
    {
      q: `Is there a CriLam dealer in ${city.name}?`,
      a: `CriLam maintains an authorized dealer network across UP including ${city.name}. Visit /dealer for the current partner list or call ${CRILAM_FACTS.phone} for the nearest showroom.`,
    },
    {
      q: `What's the warranty on CriLam boards purchased in ${city.name}?`,
      a: `10-year written manufacturer guarantee on the PUR-bonded 6H Acrylic Board, covering lamination bond integrity, edge sealing, and finish quality — valid across India including ${city.name}.`,
    },
    {
      q: `Can I visit the CriLam factory from ${city.name}?`,
      a: `Absolutely — the CriLam factory is located at ${CRILAM_FACTS.factoryAddress}. ${city.name} customers are welcome to schedule a factory tour by contacting ${CRILAM_FACTS.phone}. See the PUR lamination line, mirror finish quality, and product library first-hand.`,
    },
  ];

  const jsonLd = [
    buildLocalBusinessSchema({ city: city.name }),
    buildFAQSchema(faqs),
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Uttar Pradesh', url: `${SITE_URL}/` },
      { name: city.name, url },
    ]),
  ];

  const relatedCities = UP_CITIES.filter((c) => c.slug !== city.slug).slice(0, 8);

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid={`city-page-${city.slug}`}>
      <SEO
        title={title}
        description={description}
        canonical={url}
        keywords={`acrylic boards ${city.name}, pre-laminated acrylic ${city.name}, CriLam ${city.name}, PUR laminate ${city.name}, mirror finish laminate ${city.name} UP`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span>Uttar Pradesh</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">{city.name}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
              <MapPin className="h-3.5 w-3.5" /> {city.name}, Uttar Pradesh
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              Pre-Laminated Acrylic Boards in {city.name}
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-4">{city.tagline}</p>
            <p className="text-white/70 max-w-3xl leading-relaxed">
              {BRAND} manufactures Pre-Laminated 6H Acrylic Boards with proprietary CriLam PUR-Bond™ technology in Kanpur — delivered factory-direct to {city.name}.
              Mirror finish · Anti-yellowing · 100% waterproof · Nail-free · 10-year written warranty. {city.hqCity ? 'This is our HQ city — walk in for a factory tour.' : `${city.distance} — typical 24-72 hour dispatch.`}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="city-cta-enquire">
                <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                  Get a {city.name} Quote <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <a href={`tel:${CRILAM_FACTS.phone.replace(/\s+/g, '')}`} data-testid="city-cta-call">
                <button className="px-6 py-3 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10 flex items-center gap-2">
                  <Phone className="h-4 w-4" /> {CRILAM_FACTS.phone}
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick info strip */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <Truck className="h-6 w-6 text-[#D4AF37] mx-auto mb-1.5" />
            <p className="text-xs font-bold text-[#004534] uppercase tracking-wider">From Factory</p>
            <p className="text-sm text-gray-700 mt-0.5">{city.distance}</p>
          </div>
          <div className="text-center">
            <ShieldCheck className="h-6 w-6 text-[#D4AF37] mx-auto mb-1.5" />
            <p className="text-xs font-bold text-[#004534] uppercase tracking-wider">Warranty</p>
            <p className="text-sm text-gray-700 mt-0.5">10-Year Written</p>
          </div>
          <div className="text-center">
            <Sparkles className="h-6 w-6 text-[#D4AF37] mx-auto mb-1.5" />
            <p className="text-xs font-bold text-[#004534] uppercase tracking-wider">Finish</p>
            <p className="text-sm text-gray-700 mt-0.5">95-98 GU Mirror</p>
          </div>
          <div className="text-center">
            <Phone className="h-6 w-6 text-[#D4AF37] mx-auto mb-1.5" />
            <p className="text-xs font-bold text-[#004534] uppercase tracking-wider">Enquiries</p>
            <p className="text-sm text-gray-700 mt-0.5">{CRILAM_FACTS.phone}</p>
          </div>
        </div>
      </section>

      {/* Why CriLam in this city */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-8 font-['Outfit']">
            Why {city.name} Interior Designers Choose CriLam
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Manufactured in UP',
                body: `${city.name} customers get the same 6H Pre-Laminated Acrylic Board that ships to Delhi, Mumbai, Bangalore — but with the shortest possible supply chain. Factory direct means no dealer markup, no old stock, always fresh production.`,
              },
              {
                title: 'CriLam PUR-Bond™ Technology',
                body: `Every CriLam board is bonded with Polyurethane Reactive adhesive — 5× stronger than water-based glue, 100% waterproof, and moisture-cured for humid ${city.name} conditions. See /nail-free-advantage.`,
              },
              {
                title: '10-Year Guarantee',
                body: 'Written manufacturer guarantee covers lamination bond, edge sealing, and finish integrity. Enforced pan-India through CriLam service partners.',
              },
              {
                title: 'AI Design Studio',
                body: `Before you order, visualize your ${city.name} kitchen or wardrobe with CriLam finishes using our AI Visualizer + Door Designer. Zero commitment, instant preview.`,
              },
              {
                title: '4 Material Families',
                body: 'Acrylic Laminate · Mica · Natural Veneer (10 species) · PU Paint — same nail-free construction across all four. Match every room to the right material.',
              },
              {
                title: 'Factory Visit Welcome',
                body: `${city.name} customers are welcome to tour the CriLam Kanpur factory. See PUR bonding, mirror finish quality, and the shade library first-hand.`,
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:border-[#D4AF37]/50 transition-colors"
              >
                <h3 className="font-bold text-[#004534] mb-2 font-['Outfit']">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product family quick access */}
      <section className="py-12 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-6 font-['Outfit']">
            Every Material Family, Delivered to {city.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Acrylic Laminate', link: '/acrylic-laminate-doors', badge: 'Flagship · Mirror' },
              { name: 'Mica CNC Doors', link: '/mica-cnc-doors', badge: '200+ Shades' },
              { name: 'Natural Veneer', link: '/veneer-nail-free-doors', badge: '10 Species' },
              { name: 'PU Paint', link: '/pu-paint-doors', badge: 'Custom Colour' },
            ].map((mat, i) => (
              <Link
                key={i}
                to={mat.link}
                className="group bg-[#F7F8F5] rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37] hover:shadow-md transition-all"
                data-testid={`city-material-${i}`}
              >
                <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-2">{mat.badge}</p>
                <h4 className="font-bold text-[#004534] mb-2 font-['Outfit'] group-hover:text-[#D4AF37]">{mat.name}</h4>
                <div className="text-xs text-gray-500 flex items-center gap-1 group-hover:text-[#D4AF37]">
                  Explore <ChevronRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-14" data-testid="city-faqs">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <HelpCircle className="h-6 w-6 text-[#D4AF37]" />
            <h2 className="text-3xl font-bold text-[#004534] font-['Outfit']">FAQs — {city.name}</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group bg-white rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37]/40 transition-colors">
                <summary className="cursor-pointer flex items-start justify-between gap-4 font-semibold text-[#004534] text-base list-none">
                  <span>{f.q}</span>
                  <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-700 leading-relaxed text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="py-10 bg-[#F7F8F5] border-t">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h3 className="text-lg font-bold text-[#004534] mb-4 font-['Outfit']">Other UP Cities Served</h3>
          <div className="flex flex-wrap gap-2">
            {relatedCities.map((c) => (
              <Link
                key={c.slug}
                to={`/acrylic-boards-${c.slug}`}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-semibold text-[#004534] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                data-testid={`related-city-${c.slug}`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">
            Ready to Order in {city.name}?
          </h3>
          <p className="text-white/70 mb-8">
            Direct-from-factory pricing · No dealer markup · 10-year written warranty · Fresh production.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="city-cta-primary">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Request {city.name} Quote <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/ai-visualizer" data-testid="city-cta-visualizer">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Try AI Visualizer <ChevronRight className="h-5 w-5 inline" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
